import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";

// ✅ Use environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CompletedProjects = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [activeTab, setActiveTab] = useState("HOME");
  const [loading, setLoading] = useState(true);

  // ==========================
  // FETCH ALL PROJECTS
  // ==========================
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/projects/completed`);
        setProjects(data);

        const defaultProject = projectId || data[0]?.project_id;
        if (defaultProject) fetchProjectDetails(defaultProject);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, []);

  // ==========================
  // FETCH SINGLE PROJECT DETAILS
  // ==========================
  const fetchProjectDetails = async (id) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE_URL}/admin/get-project/${id}`);
      setCurrentProject(data);
    } catch (error) {
      console.error("Error fetching project details:", error);
    } finally {
      setLoading(false);
    }
  };

  // ==========================
  // PROJECT SWITCH HANDLER
  // ==========================
  const handleProjectChange = (e) => {
    const id = e.target.value;
    navigate(`/completed-projects/${id}`);
    fetchProjectDetails(id);
  };

  // ==========================
  // LOADING STATE
  // ==========================
  if (loading || !currentProject) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-gray-500 text-lg">Loading project details...</p>
      </div>
    );
  }

  // ==========================
  // JSX
  // ==========================
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb + Project Selector */}
      <div className="bg-gray-50 py-6 px-4 border-b">
        <div className="container mx-auto max-w-7xl flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() => navigate("/projects")}
              className="text-blue-600 hover:text-blue-700 font-semibold"
            >
              Completed Projects
            </button>
            <span className="text-gray-400">/</span>
            <span className="text-gray-700 font-medium">
              {currentProject.name}
            </span>
          </div>
          <select
            value={currentProject.project_id}
            onChange={handleProjectChange}
            className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 bg-white"
          >
            {projects.map((proj) => (
              <option key={proj.project_id} value={proj.project_id}>
                {proj.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-16 z-40">
        <div className="container mx-auto max-w-7xl px-4 flex gap-2 overflow-x-auto">
          {["HOME", "GALLERY", "PLAN", "LOCATION", "AMENITIES"].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-6 py-4 font-semibold text-sm transition-colors ${activeTab === tab
                ? "bg-green-600 text-white"
                : "text-gray-600 hover:bg-gray-100"
                }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="container mx-auto max-w-7xl py-12 px-4">
        {/* Home Tab */}
        {activeTab === "HOME" && (
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-4">
              {currentProject.title}
            </h1>
            <p className="text-gray-600 mb-6 leading-relaxed">
              {currentProject.description}
            </p>

            <div className="bg-gradient-to-br from-blue-50 to-green-50 p-6 rounded-xl mb-8">
              <h3 className="text-xl font-bold mb-4 text-gray-800">
                Project Information
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-gray-700">
                <p>
                  <strong>RERA:</strong> {currentProject.rera}
                </p>
                <p>
                  <strong>Extent:</strong> {currentProject.extent}
                </p>
                <p>
                  <strong>Location:</strong> {currentProject.location}
                </p>
              </div>
            </div>

            <div className="flex justify-center">
              <img
                src={currentProject.image_url}
                alt={currentProject.name}
                className="rounded-xl shadow-2xl max-w-3xl w-full"
              />
            </div>
          </div>
        )}

        {/* Amenities Tab */}
        {activeTab === "AMENITIES" && (
          <div>
            <h2 className="text-3xl font-bold text-gray-800 mb-6">
              Amenities
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentProject.amenities?.split(",").map((amenity, i) => (
                <div
                  key={i}
                  className="border p-6 rounded-xl hover:border-green-500 transition"
                >
                  <h3 className="font-bold text-gray-800">{amenity}</h3>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CompletedProjects;
